import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import {useParams} from 'react-router';

import {useSingleRunModeContext} from '../../components/MultiRunWorkspace/SingleRunMode';
import {useRunIssues} from '../../components/RunSelector/hooks/useRunIssues';
import {getDatadogRum} from '../../util/datadog/datadogRum';
import {getConversionUnitFromMaxValue} from '../../util/plotHelpers';
import {type KeyVal} from '../../util/runTypes';
import {type MultiRunWorkspaceViewType} from '../views/types';
import {
  initialState,
  issuesByTimestampReducer,
} from './issuesByTimestampReducer';
import {doTimestampsByMetricQuery} from './timestampQuery';
import {type Issue, type IssueLine, type TimestampKeyTuple} from './types';
import {
  useDidRunOnCoreweave,
  useRampFlagCoreweaveIssues,
} from './useCoreweaveIssues';
import {useRunIssuesDashboard} from './useRunIssuesDashboard';
import {groupIssues} from './utils';

type ActiveRun =
  | {
      name: string;
      createdAt: string;
      displayName: string;
      state: string;
      _wandb: KeyVal;
    }
  | undefined;

type IssuesContextType = {
  activeErrorDrawer: boolean;
  activeIssues: Issue[];
  activeRun?: ActiveRun | null;
  addIssues: (issues: Issue[]) => void;
  annotationActive: boolean;
  clearIssues: () => void;
  hoveredIssue: string | null;
  fetchTimestamps: ({
    xAxis,
    zoom,
  }: {
    xAxis: string;
    zoom: [number | null, number | null];
  }) => Promise<void>;
  // These are used to populate the panels
  getIssuesByCacheKey: (cacheKey: string) => IssueLine[];
  groupedIssues: Issue[][];
  handleIssueInteraction: (
    event: 'hover' | 'unhover',
    issueId: string | null
  ) => () => void;
  iconPositions: {
    marginLeft: number;
    xMin: number;
    xMax: number;
  };
  issueRampStatus: 'enabled' | 'disabled';
  runGrafanaDashboard: string | null;
  selectedIssue: string | null;
  setActiveRun: (r: ActiveRun | null) => void;
  setSelectedIssue: (issueId: string | null) => void;
  toggleIssues: () => void;
  updateErrorIssue: (hasError: boolean) => void;
  updateIconPositions: (iconPositions: {
    marginLeft: number;
    xMin: number;
    xMax: number;
  }) => void;
  workspaceType:
    | MultiRunWorkspaceViewType
    | null
    // DO NOT USE; this is a temporary type for the run-view that we're actively deprecating.
    // To determine if in single-run mode, use useSingleRunModeContext instead.
    | 'deprecated-run-view';
};

// No-op functions with proper signatures
const noOpAddIssues = () => {};
const noOp = () => {};
const noOpSetActiveRun = () => {};
const noOpFetchTimestamps = async () => {};
const noOpGetIssuesByCacheKey = (): IssueLine[] => [];
const noOpUpdateErrorIssue = () => {};
const noOpUpdateIconPositions = () => {};
const noOpSelectIssue = () => {};
const DD_RUM = getDatadogRum();

export const IssuesContext = React.createContext<IssuesContextType>({
  activeErrorDrawer: false,
  activeIssues: [],
  activeRun: null,
  addIssues: noOpAddIssues,
  annotationActive: true,
  clearIssues: noOp,
  hoveredIssue: null,
  fetchTimestamps: noOpFetchTimestamps,
  getIssuesByCacheKey: noOpGetIssuesByCacheKey,
  groupedIssues: [],
  handleIssueInteraction: () => noOp,
  iconPositions: {marginLeft: 0, xMin: 0, xMax: 0},
  issueRampStatus: 'disabled',
  runGrafanaDashboard: null,
  selectedIssue: null,
  setActiveRun: noOpSetActiveRun,
  setSelectedIssue: noOpSelectIssue,
  toggleIssues: noOp,
  updateErrorIssue: noOpUpdateErrorIssue,
  updateIconPositions: noOpUpdateIconPositions,
  workspaceType: null,
});

function convertZoomToCacheKey(
  xAxis: string,
  zoom: [number | null, number | null]
) {
  return `${xAxis}-${zoom[0] ?? 'null'}-${zoom[1] ?? null}`;
}

export const IssuesContextProvider = ({
  children,
  workspaceType,
}: {
  children: React.ReactNode;
  workspaceType: MultiRunWorkspaceViewType | 'deprecated-run-view';
}) => {
  const {isSingleRun, run} = useSingleRunModeContext();
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  const canUseIssuesContext = useRampFlagCoreweaveIssues();
  const canFetch = isSingleRun && canUseIssuesContext;

  const [selectedIssue, setSelectedIssue] = useState<string | null>(null);
  const [hoveredIssue, setHoveredIssue] = useState<string | null>(null);
  const [activeRun, setActiveRun] = useState<ActiveRun | null>(run);

  const {ranOnCoreweave, loading: ranOnCoreweaveLoading} = useDidRunOnCoreweave(
    projectName,
    entityName,
    run?.name ?? activeRun?.name ?? '',
    canUseIssuesContext
  );

  const {data: runIssuesData, error: runIssueError} = useRunIssues({
    ranOnCoreweave,
    entityName,
    projectName,
    runName: run?.name ?? '',
  });

  const {data: runGrafanaDashboard} = useRunIssuesDashboard({
    ranOnCoreweave,
    entityName,
    projectName,
    runName: run?.name ?? activeRun?.name ?? '',
  });

  useEffect(() => {
    // Occasionally, `useSingleModeContext` will return an undefined run when
    // called, initially, but will populate later. If that happens, we want to
    // make sure to update our activeRun to match. We need to maintain the
    // active run state here, for now, because it's used by the Run Selector
    // when it opens the drawer and there is no run id in the URL.
    if (activeRun === undefined) {
      setActiveRun(run);
    }
  }, [run, activeRun]);

  const handleIssueInteraction = useCallback(
    (event: 'hover' | 'unhover', issueId: string | null) => () => {
      if (event === 'hover') {
        setHoveredIssue(issueId);
      } else {
        setHoveredIssue(null);
      }
    },
    []
  );

  const [state, dispatch] = useReducer(issuesByTimestampReducer, initialState);
  const [useIssues, setUseIssues] = useState(true);

  const [iconPositions, setIconPositions] = useState({
    marginLeft: 0,
    xMin: 0,
    xMax: 0,
  });

  const addIssues = useCallback((issues: Issue[]) => {
    if (issues.length < 1) {
      return;
    }
    dispatch({type: 'addIssues', payload: {issues: issues, since: 0}});
  }, []);

  const updateErrorIssue = useCallback((hasError: boolean) => {
    dispatch({type: 'updateIssueError', payload: {hasError}});
  }, []);

  useEffect(() => {
    if (runIssuesData && run?.name) {
      addIssues(runIssuesData);
    }
  }, [runIssuesData, addIssues, run?.name]);

  const clearIssues = useCallback(() => {
    dispatch({type: 'clearIssues'});
  }, []);

  const toggleIssues = useCallback(() => {
    setUseIssues(prev => !prev);
  }, []);

  const updateIconPositions = useCallback(
    ({
      marginLeft,
      xMin,
      xMax,
    }: Partial<{
      marginLeft: number;
      xMin: number;
      xMax: number;
    }>) => {
      const canUpdate = [
        iconPositions.marginLeft !== marginLeft,
        iconPositions.xMin !== xMin,
        iconPositions.xMax !== xMax,
      ].some(condition => !!condition);

      if (canUpdate) {
        setIconPositions(prev => ({
          marginLeft: marginLeft ?? prev.marginLeft,
          xMin: xMin ?? prev.xMin,
          xMax: xMax ?? prev.xMax,
        }));
      }
    },
    [iconPositions]
  );

  const fetchTimestamps = useCallback(
    async ({
      xAxis,
      zoom,
    }: {
      xAxis: string;
      zoom: [number | null, number | null];
    }) => {
      const cacheKey = convertZoomToCacheKey(xAxis, zoom);

      // prevent fetching any cache key zoom combo we already have or are currently fetching
      if (
        state.timestamps[cacheKey]?.keyByTimestamp?.length > 0 ||
        state.timestamps[cacheKey]?.loading ||
        !run?.name
      ) {
        return;
      }

      const propTarget = xAxis === '_step' ? 'Step' : 'X';
      const zoomObj: {
        minStep?: number;
        maxStep?: number;
        minX?: number;
        maxX?: number;
      } = {};
      if (zoom[0] != null) {
        zoomObj[`min${propTarget}`] = Math.floor(zoom[0]);
      }
      if (zoom[1] != null) {
        zoomObj[`max${propTarget}`] = Math.ceil(zoom[1]);
      }
      dispatch({type: 'fetchTimestamps-start', payload: {xAxis, cacheKey}});
      /**
       * The _absolute_runtime axis is not supported by the issuesTimestamp Query, so we need to transform it to the _runtime axis.
       */
      const safeXAxis = xAxis === '_absolute_runtime' ? '_runtime' : xAxis;
      try {
        const {data} = await doTimestampsByMetricQuery({
          entityName,
          projectName,
          runName: run?.name,
          bucketedHistorySpecs: [
            JSON.stringify({
              keys: ['_timestamp', safeXAxis],
              samples: 1000,
              bins: 1000,
              ...zoomObj,
              xAxis: safeXAxis,
              metricTransformations: {},
            }),
          ],
        });
        const history = data.project?.run?.bucketedHistory;
        if (!history || history.length !== 1) {
          DD_RUM.addAction('Issues_Observability_Error', {
            message:
              'Returned incorrect number of bucketedHistories from issuesTimestamp Query',
            xAxis,
            safeXAxis,
            cacheKey,
            zoom: zoomObj,
          });
          return;
        }
        const timestampTuples: TimestampKeyTuple[] = [];

        const mapFnToAxis: Record<
          string,
          ({
            timestamp,
            xValue,
            index,
            maxVal,
          }: {
            timestamp: number;
            maxVal?: number;
            xValue: number;
            index: number;
          }) => void
        > = {
          // relative time (process)
          _runtime: ({timestamp, xValue, maxVal}) => {
            if (typeof maxVal !== 'number') {
              DD_RUM.addAction('Issues_Observability_Error', {
                message: 'No Max val in _runtime issue transformation',
                xAxis,
                safeXAxis,
                cacheKey,
                zoom: zoomObj,
                timestamp,
                xValue,
                maxVal,
              });
              return;
            }
            const unit = getConversionUnitFromMaxValue(maxVal);
            timestampTuples.push([timestamp, xValue / unit]);
          },
          // relative time (wall clock)
          _absolute_runtime: ({timestamp, xValue, maxVal}) => {
            if (typeof maxVal !== 'number') {
              DD_RUM.addAction('Issues_Observability_Error', {
                message: 'No Max val in _runtime issue transformation',
                xAxis,
                safeXAxis,
                cacheKey,
                zoom: zoomObj,
                timestamp,
                xValue,
                maxVal,
              });
              return;
            }
            const unit = getConversionUnitFromMaxValue(maxVal);
            timestampTuples.push([timestamp, xValue / unit]);
          },
          // absolute time (wall clock)
          _timestamp: ({timestamp, xValue}) => {
            // convert the value to milliseconds
            timestampTuples.push([timestamp, xValue * 1000]);
          },
          // non-time based metrics
          default: ({timestamp, xValue, index}) => {
            if (
              index > 0 &&
              index - 1 < timestampTuples.length &&
              timestampTuples[index - 1] &&
              (timestamp <= timestampTuples[index - 1][0] ||
                xValue <= timestampTuples[index - 1][1])
            ) {
              DD_RUM.addAction('Issues_Observability_Error', {
                message:
                  'Returned unsorted on nonmonotonic values from issuesTimestamp Query',
                xAxis,
                safeXAxis,
                cacheKey,
                zoom: zoomObj,
                timestamp,
                xValue,
                index,
              });
            }

            // need to convert the timestamps coming back to mS
            timestampTuples.push([timestamp, xValue]);
          },
        };

        const maxFn: Record<string, (history: any) => number | undefined> = {
          _runtime: () => {
            return Math.max(
              ...history[0].map((curr: any) => curr['_runtimeLast'])
            );
          },
          _absolute_runtime: () => {
            return Math.max(
              ...history[0].map(
                (curr: any) =>
                  (curr['_timestampLast'] * 1000 - creationTimestamp) / 1000
              )
            );
          },
          _timestamp: () => undefined,
          default: () => undefined,
        };

        const creationTimestamp = new Date(run?.createdAt).getTime();

        // add the creation timestamp to the timestamp tuples if the xAxis is a time-based metric
        // this is to ensure that we have a reference point for when the run started so that if the run doesn't plot a metric
        // until a bit of time has passed, we have a reference point to use for before issues.
        if (
          xAxis === '_runtime' ||
          xAxis === '_absolute_runtime' ||
          xAxis === '_timestamp' ||
          xAxis === '_step'
        ) {
          timestampTuples.push([creationTimestamp, 0]);
        }
        const maxVal = maxFn[xAxis]?.(history[0]) ?? undefined;
        const mapFn = mapFnToAxis[xAxis] ?? mapFnToAxis['default'];

        history[0]?.forEach((curr: any, index: number) => {
          const currentTimestampMs = curr._timestampLast * 1000;
          const xValue = curr[`${safeXAxis}Last`];
          if (currentTimestampMs == null || xValue == null) {
            DD_RUM.addAction('Issues_Observability_Error', {
              message: 'Returned incorrect data from issuesTimestamp Query',
              xAxis,
              safeXAxis,
              cacheKey,
              zoom: zoomObj,
              timestamp: currentTimestampMs,
              xValue,
            });
            return;
          }

          mapFn({
            timestamp: currentTimestampMs,
            xValue,
            index,
            maxVal,
          });
        });

        dispatch({
          type: 'fetchTimestamps-success',
          payload: {xAxis, cacheKey, results: timestampTuples},
        });
      } catch (err) {
        dispatch({
          type: 'fetchTimestamps-error',
          payload: {xAxis, cacheKey, error: err as Error},
        });
      }
    },
    [state.timestamps, entityName, projectName, run?.name, run?.createdAt]
  );

  /**
   * Find the issues for a given cache key. If the cache isn't found, that means we're fetching and we should use the nearest matching zoom range temporarily.
   * NOTE: Extend this algorith to find the nearest match.
   */
  const getIssuesByCacheKey = useCallback(
    (cacheKey: string) => {
      if (!state.issuesByCacheKey[cacheKey]) {
        const [xAxis] = cacheKey.split('-');
        return state.issuesByCacheKey[`${xAxis}-null-null`] ?? [];
      }
      return state.issuesByCacheKey[cacheKey] ?? [];
    },
    [state.issuesByCacheKey]
  );

  /**
   * If we get an error back from the run selector and we're in the single run workspace, then we need to update the state so that the drawer becomes aware that the fetch has failed
   */
  useEffect(() => {
    const didRunOnCoreweave =
      ranOnCoreweave &&
      !ranOnCoreweaveLoading &&
      !!run?.name &&
      !!activeRun?.name;
    if (
      isSingleRun &&
      (runIssueError || !didRunOnCoreweave) &&
      !state.activeFetchErrorDrawer
    ) {
      updateErrorIssue(true);
    }
    if (didRunOnCoreweave && !runIssueError) {
      updateErrorIssue(false);
    }
  }, [
    runIssueError,
    updateErrorIssue,
    state.activeFetchErrorDrawer,
    isSingleRun,
    ranOnCoreweave,
    run?.name,
    activeRun?.name,
    ranOnCoreweaveLoading,
  ]);

  const disabledValue = useMemo(() => {
    return {
      activeErrorDrawer: false,
      activeIssues: state.activeFetchErrorDrawer ? [] : state.issues,
      activeRun: null,
      activeRunInfo: null,
      addIssues: noOpAddIssues,
      annotationActive: false,
      clearIssues: clearIssues,
      fetchTimestamps: noOpFetchTimestamps,
      getIssuesByCacheKey: noOpGetIssuesByCacheKey,
      handleIssueInteraction: () => () => {},
      hoveredIssue: null,
      iconPositions: {
        marginLeft: 0,
        xMin: 0,
        xMax: 0,
      },
      issueRampStatus: 'disabled' as const,
      groupedIssues: groupIssues(state.issues),
      runGrafanaDashboard: '',
      selectedIssue: null,
      setActiveRun: noOpSetActiveRun,
      setSelectedIssue: noOp,
      toggleIssues: noOp,
      updateErrorIssue: noOpUpdateErrorIssue,
      updateIconPositions: noOpUpdateIconPositions,
      workspaceType,
    };
  }, [clearIssues, state.issues, workspaceType, state.activeFetchErrorDrawer]);

  const val = useMemo(
    () => ({
      activeErrorDrawer: state.activeFetchErrorDrawer,
      activeIssues: state.activeFetchErrorDrawer ? [] : state.issues,
      activeRun,
      addIssues,
      annotationActive: useIssues,
      clearIssues,
      fetchTimestamps: canFetch ? fetchTimestamps : noOpFetchTimestamps,
      getIssuesByCacheKey: canFetch
        ? getIssuesByCacheKey
        : noOpGetIssuesByCacheKey,
      groupedIssues: groupIssues(state.issues),
      handleIssueInteraction,
      hoveredIssue,
      iconPositions,
      issueRampStatus: canFetch ? ('enabled' as const) : ('disabled' as const),
      runGrafanaDashboard: runGrafanaDashboard ?? '',
      selectedIssue,
      setActiveRun,
      setSelectedIssue,
      toggleIssues,
      updateErrorIssue,
      updateIconPositions,
      workspaceType,
    }),
    [
      activeRun,
      addIssues,
      canFetch,
      clearIssues,
      fetchTimestamps,
      getIssuesByCacheKey,
      handleIssueInteraction,
      hoveredIssue,
      iconPositions,
      runGrafanaDashboard,
      selectedIssue,
      setActiveRun,
      setSelectedIssue,
      state.activeFetchErrorDrawer,
      state.issues,
      toggleIssues,
      updateErrorIssue,
      updateIconPositions,
      useIssues,
      workspaceType,
    ]
  );

  return (
    <IssuesContext.Provider value={canUseIssuesContext ? val : disabledValue}>
      {children}
    </IssuesContext.Provider>
  );
};

export const useIssuesContext = () => {
  const val = React.useContext(IssuesContext);
  if (!val) {
    DD_RUM.addAction('Issues_Observability_Error', {
      message: 'useIssuesContext must be used within a IssuesContextProvider',
    });
  }
  return val;
};
